import React, { useCallback, useEffect, useMemo, useState } from "react"
import {
    Paper,
    Box,
    Tabs,
    Tab,
    TextField,
    MenuItem,
    Button,
    LinearProgress,
    Chip,
    Typography,
    TableContainer,
} from "@mui/material"
import { DataGrid } from "@mui/x-data-grid"
import RefreshIcon from "@mui/icons-material/Refresh"
import { toast } from "react-toastify"
import moment from "moment"
import PaginationTable from "../../Components/Pagination"
import {
    fetchActivitySummary,
    fetchActivityLogs,
    fetchActivityUsers,
    fetchActivityTrend,
} from "../../Actions/commonController"

const TIER_LABELS = {
    "1": "Super Admin",
    "2": "Admin",
    "3": "HR",
    "4": "Employee",
}

const tierName = (tier) => TIER_LABELS[String(tier)] || (tier ? `Tier ${tier}` : "—")

const formatStamp = (value) =>
    value ? moment(String(value).replace("T", " ")).format("DD/MM/YYYY HH:mm:ss") : "—"

const today = () => moment().format("YYYY-MM-DD")

const OUTCOME_COLOR = {
    Success: "success",
    Denied: "warning",
    Error: "error",
}

// --- small presentational helpers -------------------------------------------

const StatCard = ({ label, value, tone }) => (
    <Box
        sx={{
            flex: "1 1 150px",
            minWidth: 150,
            padding: "16px 18px",
            borderRadius: "var(--radius-md, 8px)",
            border: "1px solid var(--divider-color, rgba(0,0,0,0.12))",
            backgroundColor: "var(--paper-bg-color, #fff)",
        }}
    >
        <Typography
            sx={{
                fontSize: 11,
                letterSpacing: "0.7px",
                textTransform: "uppercase",
                color: "var(--muted-color, #6b7280)",
            }}
        >
            {label}
        </Typography>
        <Typography sx={{ fontSize: 26, fontWeight: 600, marginTop: "4px", color: tone || "inherit" }}>
            {value}
        </Typography>
    </Box>
)

const BarList = ({ items, emptyText }) => {
    if (!items.length) {
        return (
            <Typography sx={{ padding: "16px 0", color: "var(--muted-color, #6b7280)" }}>
                {emptyText}
            </Typography>
        )
    }

    const max = Math.max(...items.map((i) => Number(i.value) || 0)) || 1

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "9px" }}>
            {items.map((item) => (
                <Box
                    key={item.label}
                    sx={{
                        display: "grid",
                        gridTemplateColumns: "minmax(90px, 160px) 1fr 56px",
                        gap: "10px",
                        alignItems: "center",
                        fontSize: 13,
                    }}
                >
                    <span>{item.label}</span>
                    <Box
                        sx={{
                            height: 8,
                            borderRadius: "4px",
                            backgroundColor: "var(--divider-color, rgba(0,0,0,0.08))",
                            overflow: "hidden",
                        }}
                    >
                        <Box
                            sx={{
                                height: "100%",
                                width: `${((Number(item.value) || 0) / max) * 100}%`,
                                backgroundColor: "var(--primary-color, #2596BE)",
                            }}
                        />
                    </Box>
                    <span style={{ textAlign: "right", color: "var(--muted-color, #6b7280)" }}>
                        {item.value}
                    </span>
                </Box>
            ))}
        </Box>
    )
}

const Panel = ({ title, children }) => (
    <Box
        sx={{
            flex: "1 1 320px",
            minWidth: 300,
            padding: "18px",
            borderRadius: "var(--radius-md, 8px)",
            border: "1px solid var(--divider-color, rgba(0,0,0,0.12))",
        }}
    >
        <Typography
            sx={{
                fontSize: 12,
                letterSpacing: "0.7px",
                textTransform: "uppercase",
                color: "var(--muted-color, #6b7280)",
                marginBottom: "14px",
            }}
        >
            {title}
        </Typography>
        {children}
    </Box>
)

// --- page --------------------------------------------------------------------

export default function ActivityTracker() {
    const [tab, setTab] = useState(0)
    const [loader, setLoader] = useState(false)

    // Daily tab
    const [date, setDate] = useState(today())
    const [summary, setSummary] = useState(null)
    const [trend, setTrend] = useState([])

    // Log tab
    const [fromDate, setFromDate] = useState(today())
    const [toDate, setToDate] = useState(today())
    const [category, setCategory] = useState("")
    const [outcome, setOutcome] = useState("")
    const [changesOnly, setChangesOnly] = useState("")
    const [search, setSearch] = useState("")
    const [logs, setLogs] = useState([])
    const [logTotal, setLogTotal] = useState(0)
    const [page, setPage] = useState(1)
    const [quantity, setQuantity] = useState(25)

    // Adoption tab
    const [days, setDays] = useState(30)
    const [adoption, setAdoption] = useState(null)

    const loadDaily = useCallback(async () => {
        setLoader(true)
        try {
            const [summaryRes, trendRes] = await Promise.all([
                fetchActivitySummary(date),
                fetchActivityTrend(14),
            ])

            if (summaryRes.status === "success") setSummary(summaryRes.data)
            else toast.error(summaryRes.message || "Could not load activity summary")

            if (trendRes.status === "success") setTrend(trendRes.data || [])
        } finally {
            setLoader(false)
        }
    }, [date])

    const loadLogs = useCallback(async () => {
        setLoader(true)
        try {
            const res = await fetchActivityLogs({
                date: fromDate,
                toDate: toDate,
                page,
                limit: quantity,
                category: category || undefined,
                outcome: outcome || undefined,
                changesOnly: changesOnly || undefined,
                search: search.trim() || undefined,
            })

            if (res.status === "success") {
                setLogs(res.data || [])
                setLogTotal(res.total || 0)
            } else {
                toast.error(res.message || "Could not load activity log")
            }
        } finally {
            setLoader(false)
        }
    }, [fromDate, toDate, page, quantity, category, outcome, changesOnly, search])

    const loadAdoption = useCallback(async () => {
        setLoader(true)
        try {
            const res = await fetchActivityUsers(days)
            if (res.status === "success") setAdoption(res.data)
            else toast.error(res.message || "Could not load user activity")
        } finally {
            setLoader(false)
        }
    }, [days])

    useEffect(() => {
        if (tab === 0) loadDaily()
    }, [tab, loadDaily])

    useEffect(() => {
        if (tab === 1) loadLogs()
    }, [tab, loadLogs])

    useEffect(() => {
        if (tab === 2) loadAdoption()
    }, [tab, loadAdoption])

    const refresh = () => {
        if (tab === 0) loadDaily()
        else if (tab === 1) loadLogs()
        else loadAdoption()
    }

    const categoryOptions = useMemo(
        () => (summary?.byCategory || []).map((c) => c.category),
        [summary]
    )

    const hourBars = useMemo(() => {
        const byHour = summary?.byHour || []
        return Array.from({ length: 24 }, (_, hour) => {
            const found = byHour.find((h) => Number(h.hour) === hour)
            return {
                label: `${String(hour).padStart(2, "0")}:00`,
                value: found ? Number(found.total) : 0,
            }
        })
    }, [summary])

    const logColumns = [
        {
            field: "timestamp",
            headerName: "Time",
            width: 170,
            renderCell: (params) => formatStamp(params.value),
        },
        {
            field: "name",
            headerName: "Who",
            width: 210,
            renderCell: (params) => (
                <Box sx={{ lineHeight: 1.3, paddingTop: "8px" }}>
                    <div style={{ fontWeight: 600 }}>
                        {params.row.name || params.row.username || "Not signed in"}
                    </div>
                    <div style={{ fontSize: 12, opacity: 0.7 }}>
                        {params.row.empid || "—"}
                        {params.row.tier ? ` · ${tierName(params.row.tier)}` : ""}
                    </div>
                </Box>
            ),
        },
        { field: "action", headerName: "Action", width: 240 },
        { field: "category", headerName: "Area", width: 150 },
        { field: "details", headerName: "Details", width: 300 },
        {
            field: "outcome",
            headerName: "Result",
            width: 110,
            renderCell: (params) => (
                <Chip
                    size="small"
                    label={params.value || "—"}
                    color={OUTCOME_COLOR[params.value] || "default"}
                    variant="outlined"
                />
            ),
        },
        { field: "ip", headerName: "IP address", width: 140 },
    ]

    const adoptionColumns = [
        {
            field: "name",
            headerName: "Employee",
            width: 230,
            renderCell: (params) => (
                <Box sx={{ lineHeight: 1.3, paddingTop: "8px" }}>
                    <div style={{ fontWeight: 600 }}>{params.row.name || params.row.username || "—"}</div>
                    <div style={{ fontSize: 12, opacity: 0.7 }}>{params.row.empid}</div>
                </Box>
            ),
        },
        {
            field: "tier",
            headerName: "Role",
            width: 130,
            renderCell: (params) => tierName(params.value),
        },
        { field: "logins", headerName: "Logins", width: 100 },
        { field: "activeDays", headerName: "Active days", width: 120 },
        { field: "actions", headerName: "Total actions", width: 130 },
        {
            field: "lastSeen",
            headerName: "Last seen",
            width: 180,
            renderCell: (params) => formatStamp(params.value),
        },
    ]

    const adoptionPercent = adoption?.totalEmployees
        ? Math.round((adoption.activeEmployees / adoption.totalEmployees) * 100)
        : 0

    return (
        <Paper className="pagePaper" sx={{ width: "100%", maxWidth: "95%", boxSizing: "border-box" }}>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: "12px",
                }}
            >
                <span className="info">
                    <h5>Activity Tracker</h5>
                </span>
                <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={refresh}>
                    Refresh
                </Button>
            </Box>

            <Tabs
                value={tab}
                onChange={(e, value) => setTab(value)}
                sx={{ marginTop: "8px", borderBottom: "1px solid var(--divider-color, rgba(0,0,0,0.12))" }}
            >
                <Tab label="Daily activity" />
                <Tab label="Full log" />
                <Tab label="Who's using it" />
            </Tabs>

            {loader && <LinearProgress sx={{ marginTop: "2px" }} />}

            {/* ---------------- Daily ---------------- */}
            {tab === 0 && (
                <Box sx={{ paddingTop: "22px" }}>
                    <TextField
                        type="date"
                        size="small"
                        label="Date"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ marginBottom: "20px" }}
                    />

                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: "12px", marginBottom: "20px" }}>
                        <StatCard label="Total actions" value={summary?.totals.actions ?? 0} />
                        <StatCard
                            label="Users active"
                            value={summary?.totals.activeUsers ?? 0}
                            tone="var(--success-color, #2e7d32)"
                        />
                        <StatCard label="Logins" value={summary?.totals.logins ?? 0} />
                        <StatCard
                            label="Failed logins"
                            value={summary?.totals.failedLogins ?? 0}
                            tone={summary?.totals.failedLogins ? "var(--error-color, #d32f2f)" : undefined}
                        />
                        <StatCard
                            label="Records changed"
                            value={summary?.totals.changes ?? 0}
                            tone="var(--warning-color, #ed6c02)"
                        />
                    </Box>

                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: "14px", marginBottom: "20px" }}>
                        <Panel title="Activity by area">
                            <BarList
                                items={(summary?.byCategory || []).map((c) => ({
                                    label: c.category,
                                    value: Number(c.total),
                                }))}
                                emptyText="No activity on this date"
                            />
                        </Panel>
                        <Panel title="Activity by hour">
                            <BarList items={hourBars} emptyText="No activity on this date" />
                        </Panel>
                    </Box>

                    <Panel title="Last 14 days">
                        <BarList
                            items={trend.map((t) => ({
                                label: `${moment(String(t.logdate).slice(0, 10)).format("DD MMM")} · ${t.users} users`,
                                value: Number(t.actions),
                            }))}
                            emptyText="No activity recorded yet"
                        />
                    </Panel>

                    <Typography sx={{ marginTop: "24px", marginBottom: "10px", fontWeight: 600 }}>
                        Most active users on this date
                    </Typography>
                    <TableContainer sx={{ width: "100%" }}>
                        <DataGrid
                            className="data-grid-filter-toolbar"
                            sx={{ height: "420px" }}
                            rows={summary?.topUsers || []}
                            columns={adoptionColumns.filter((c) => c.field !== "activeDays")}
                            getRowId={(row) => row.empid}
                            disableColumnMenu
                            disableRowSelectionOnClick
                            hideFooter
                        />
                    </TableContainer>
                </Box>
            )}

            {/* ---------------- Full log ---------------- */}
            {tab === 1 && (
                <Box sx={{ paddingTop: "22px" }}>
                    <Box
                        sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "12px",
                            alignItems: "center",
                            marginBottom: "18px",
                        }}
                    >
                        <TextField
                            type="date"
                            size="small"
                            label="From"
                            value={fromDate}
                            onChange={(e) => {
                                setPage(1)
                                setFromDate(e.target.value)
                            }}
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            type="date"
                            size="small"
                            label="To"
                            value={toDate}
                            onChange={(e) => {
                                setPage(1)
                                setToDate(e.target.value)
                            }}
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            select
                            size="small"
                            label="Area"
                            value={category}
                            onChange={(e) => {
                                setPage(1)
                                setCategory(e.target.value)
                            }}
                            sx={{ minWidth: 160 }}
                        >
                            <MenuItem value="">All areas</MenuItem>
                            {categoryOptions.map((c) => (
                                <MenuItem key={c} value={c}>
                                    {c}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            select
                            size="small"
                            label="Result"
                            value={outcome}
                            onChange={(e) => {
                                setPage(1)
                                setOutcome(e.target.value)
                            }}
                            sx={{ minWidth: 130 }}
                        >
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="Success">Success</MenuItem>
                            <MenuItem value="Denied">Denied</MenuItem>
                            <MenuItem value="Error">Error</MenuItem>
                        </TextField>
                        <TextField
                            select
                            size="small"
                            label="Type"
                            value={changesOnly}
                            onChange={(e) => {
                                setPage(1)
                                setChangesOnly(e.target.value)
                            }}
                            sx={{ minWidth: 150 }}
                        >
                            <MenuItem value="">Everything</MenuItem>
                            <MenuItem value="true">Changes only</MenuItem>
                        </TextField>
                        <TextField
                            size="small"
                            label="Search"
                            placeholder="Name, employee ID, action…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    setPage(1)
                                    loadLogs()
                                }
                            }}
                            sx={{ minWidth: 240, flex: 1 }}
                        />
                        <Button
                            variant="outlined"
                            size="medium"
                            onClick={() => {
                                setPage(1)
                                loadLogs()
                            }}
                        >
                            Apply
                        </Button>
                    </Box>

                    <TableContainer sx={{ width: "100%" }}>
                        <DataGrid
                            className="data-grid-filter-toolbar"
                            sx={{ height: "560px" }}
                            rows={logs}
                            columns={logColumns}
                            getRowId={(row) => row.id}
                            disableColumnMenu
                            disableRowSelectionOnClick
                            loading={loader}
                            paginationMode="server"
                            hideFooterPagination
                            rowCount={logTotal}
                        />
                        <PaginationTable
                            totalItems={logTotal}
                            itemsPerPage={quantity}
                            onPageChange={(newPage) => setPage(newPage)}
                            onItemsPerPageChange={(newQuantity) => {
                                setQuantity(newQuantity)
                                setPage(1)
                            }}
                        />
                    </TableContainer>
                </Box>
            )}

            {/* ---------------- Adoption ---------------- */}
            {tab === 2 && (
                <Box sx={{ paddingTop: "22px" }}>
                    <TextField
                        select
                        size="small"
                        label="Period"
                        value={days}
                        onChange={(e) => setDays(Number(e.target.value))}
                        sx={{ minWidth: 180, marginBottom: "20px" }}
                    >
                        <MenuItem value={7}>Last 7 days</MenuItem>
                        <MenuItem value={30}>Last 30 days</MenuItem>
                        <MenuItem value={90}>Last 90 days</MenuItem>
                        <MenuItem value={180}>Last 180 days</MenuItem>
                    </TextField>

                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: "12px", marginBottom: "20px" }}>
                        <StatCard label="Active employees" value={adoption?.totalEmployees ?? 0} />
                        <StatCard
                            label="Used the system"
                            value={adoption?.activeEmployees ?? 0}
                            tone="var(--success-color, #2e7d32)"
                        />
                        <StatCard
                            label="Never signed in"
                            value={adoption?.neverUsed ?? 0}
                            tone={adoption?.neverUsed ? "var(--error-color, #d32f2f)" : undefined}
                        />
                        <StatCard
                            label="Adoption"
                            value={`${adoptionPercent}%`}
                            tone={
                                adoptionPercent >= 50
                                    ? "var(--success-color, #2e7d32)"
                                    : "var(--warning-color, #ed6c02)"
                            }
                        />
                    </Box>

                    <TableContainer sx={{ width: "100%" }}>
                        <DataGrid
                            className="data-grid-filter-toolbar"
                            sx={{ height: "560px" }}
                            rows={adoption?.users || []}
                            columns={adoptionColumns}
                            getRowId={(row) => row.empid}
                            disableColumnMenu
                            disableRowSelectionOnClick
                            loading={loader}
                            pageSizeOptions={[25, 50, 100]}
                            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                        />
                    </TableContainer>
                </Box>
            )}
        </Paper>
    )
}
